/**
 * Survey structural-validation client helpers.
 *
 * Mirrors the backend's referential-integrity report (services_fastapi/
 * survey_validation_service.py). The backend is the authoritative validator; these helpers
 * drive the pre-save modal and the dry-run /validate endpoint. All fix logic lives
 * server-side — the UI only ferries fix descriptors back.
 */

export type IssueSeverity = 'error' | 'warning';

export interface ValidationIssue {
  code: string;
  severity: IssueSeverity;
  question_id: string | null;
  question_index: number | null;
  field: string;
  message: string;
  target: string | null;
  auto_fixable: boolean;
  fix: Record<string, unknown> | null;
  requires_typed_confirmation: boolean;
}

export interface ValidationReport {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

export interface ValidateResult {
  report: ValidationReport;
  fixed_questions?: unknown[];
}

const EMPTY_REPORT: ValidationReport = { errors: [], warnings: [] };

/** Thrown by updateSurveyQuestions when the save is blocked by a 422 validation response. */
export class SurveyValidationError extends Error {
  report: ValidationReport;
  canOverride: boolean;

  constructor(report: ValidationReport, canOverride: boolean, message = 'validation_failed') {
    super(message);
    this.name = 'SurveyValidationError';
    this.report = report;
    this.canOverride = canOverride;
    // Restore prototype chain for instanceof across transpile targets.
    Object.setPrototypeOf(this, SurveyValidationError.prototype);
  }
}

/** True when the report has no errors and no warnings — a save can proceed silently. */
export function reportIsClean(report: ValidationReport | null | undefined): boolean {
  if (!report) return true;
  return (report.errors?.length ?? 0) === 0 && (report.warnings?.length ?? 0) === 0;
}

/** True when the report contains at least one blocking error. */
export function hasBlockingErrors(report: ValidationReport | null | undefined): boolean {
  return (report?.errors?.length ?? 0) > 0;
}

/**
 * The report's errors as one sentence for a plain error banner.
 *
 * For the paths that have no modal to open — a create-time rejection saved nothing, so there is
 * nothing to confirm or override, only something to tell the author.
 */
export function describeReportErrors(
  report: ValidationReport | null | undefined,
  fallback: string,
): string {
  const messages = (report?.errors ?? []).map((issue) => issue.message).filter(Boolean);
  return messages.length > 0 ? messages.join(' ') : fallback;
}

/** The fix descriptors for every auto-fixable issue (errors + warnings). */
export function autoFixableFixes(report: ValidationReport): Record<string, unknown>[] {
  return [...(report.errors ?? []), ...(report.warnings ?? [])]
    .filter((i) => i.auto_fixable && i.fix)
    .map((i) => i.fix as Record<string, unknown>);
}

/** True when any warning needs a typed "CONFIRM" before overriding (live-response edits). */
export function requiresTypedConfirmation(report: ValidationReport): boolean {
  return (report.warnings ?? []).some((w) => w.requires_typed_confirmation);
}

/** The number of blocking structural errors — the count that gates activation. */
export function errorCount(report: ValidationReport | null | undefined): number {
  return report?.errors?.length ?? 0;
}

/**
 * Group every issue (errors + warnings) by the question it belongs to, so the editor can render a
 * per-question marker. Issues without a question_id (survey-level) are dropped — they have no
 * inline anchor. Errors sort before warnings within a question so the badge reflects the worst.
 */
export function issuesByQuestionId(
  report: ValidationReport | null | undefined
): Map<string, ValidationIssue[]> {
  const byId = new Map<string, ValidationIssue[]>();
  if (!report) return byId;
  for (const issue of [...(report.errors ?? []), ...(report.warnings ?? [])]) {
    if (!issue.question_id) continue;
    const list = byId.get(issue.question_id) ?? [];
    list.push(issue);
    byId.set(issue.question_id, list);
  }
  return byId;
}

/**
 * Dry-run structural validation (no write). When opts.applyAutoFixes is set, the server
 * applies every safe dead-pointer removal and returns the cleaned `fixed_questions` plus a
 * re-validated report. An explicit opts.fixes subset is applied instead when provided.
 */
export async function validateSurvey(
  surveyId: number,
  questions: unknown[],
  blocks?: unknown[] | null,
  opts?: { applyAutoFixes?: boolean; fixes?: Record<string, unknown>[] }
): Promise<ValidateResult> {
  const body: Record<string, unknown> = { questions };
  if (blocks !== undefined) body.blocks = blocks;
  if (opts?.applyAutoFixes) body.apply_auto_fixes = true;
  if (opts?.fixes && opts.fixes.length) body.fixes = opts.fixes;

  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/validate`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData?.detail;
    throw new Error(
      (detail && typeof detail === 'object' ? detail.error : detail) ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }

  const data = await response.json();
  return { report: data.report ?? EMPTY_REPORT, fixed_questions: data.fixed_questions };
}
